/**
 * Evora Theme System
 *
 * 4 Theme Variants:
 * - TenantLightTheme (default)
 * - TenantDarkTheme
 * - AdminLightTheme
 * - AdminDarkTheme
 *
 * Admin themes use Storm Blue as primary for visual distinction from Tenant themes.
 */

import { createTheme } from '@mui/material/styles';
import {
  EVORA_COLORS,
  TENANT_LIGHT_COLORS,
  TENANT_DARK_COLORS,
  ADMIN_LIGHT_COLORS,
  ADMIN_DARK_COLORS,
} from './colors';

// Shared typography configuration
const sharedTypography = {
  fontFamily: ['Poppins', 'Futura PT', 'Prompt'].join(','),
  body1: {
    fontSize: '0.9rem',
  },
  body2: {
    fontSize: '0.8rem',
  },
  subtitle2: {
    fontSize: '0.75rem',
  },
};

// Shared component overrides (non-color specific)
const sharedComponents = {
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        fontSize: '0.85rem',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        fontSize: '0.8rem',
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      asterisk: {
        color: '#db3131',
        '&$error': {
          color: '#db3131',
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => ({
        fontWeight: 600,
        backgroundColor: theme.palette.background.tableHeader,
        borderBottom: `2px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:nth-of-type(even):not(.MuiTableRow-head)': {
          backgroundColor: theme.palette.background.tableAltRow,
        },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      selectLabel: {
        marginTop: 'auto',
      },
      displayedRows: {
        marginTop: 'auto',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          display: 'none',
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      },
    },
  },
  MuiCollapse: {
    defaultProps: {
      timeout: 250,
    },
  },
  // Evora-styled alerts: a paper card with a severity accent rail, brand
  // severity colours, a filled icon, a bold title and a muted message.
  // Applies to every <Alert> app-wide (toasts + inline), across all 4 themes.
  MuiAlert: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const dark = theme.palette.mode === 'dark';
        const sev = ownerState.severity || 'info';
        // Accents tuned per theme for contrast on the card background.
        const accent = {
          success: dark ? '#ABBB7D' : '#7F9450',
          info: dark ? '#7F9FBF' : '#38546D',
          warning: '#ED6C02',
          error: '#E1483B',
        }[sev] || theme.palette.info.main;

        const base = {
          position: 'relative',
          borderRadius: 12,
          alignItems: 'flex-start',
          padding: '12px 40px 12px 15px',
          borderLeft: `5px solid ${accent}`,
          '& .MuiAlert-icon': {
            color: accent,
            marginRight: '12px',
            marginTop: '1px',
            padding: 0,
            opacity: 1,
          },
          '& .MuiAlert-message': {
            padding: 0,
            color: theme.palette.text.secondary,
            fontSize: '0.8rem',
            lineHeight: 1.45,
          },
          '& .MuiAlertTitle-root': {
            fontWeight: 600,
            fontSize: '0.86rem',
            lineHeight: 1.3,
            marginBottom: '2px',
            color: theme.palette.text.primary,
          },
          '& .MuiAlert-action': {
            paddingTop: 0,
            marginRight: '-4px',
            color: theme.palette.text.secondary,
          },
        };

        // Standard variant becomes the branded paper card; filled/outlined
        // keep their own fill and just gain the accent rail + layout.
        const variant = ownerState.variant || 'standard';
        if (variant === 'standard') {
          return {
            ...base,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: `5px solid ${accent}`,
            boxShadow: dark
              ? '0 10px 26px rgba(0,0,0,0.42)'
              : '0 8px 24px rgba(20,28,48,0.16)',
          };
        }
        return base;
      },
    },
  },
};

// Navigation component styling factory
const createNavComponents = (colors) => ({
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.nav,
        borderRight: 'none',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: '24px',
        margin: '2px 12px',
        paddingLeft: '16px',
        paddingRight: '16px',
        transition: 'all 200ms ease-in-out',
        '&:hover': {
          backgroundColor: colors.background.navHover,
        },
        '&.Mui-selected': {
          backgroundColor: colors.background.navActive,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '20%',
            bottom: '20%',
            width: '4px',
            borderRadius: '0 4px 4px 0',
            backgroundColor: colors.primary,
          },
          '&:hover': {
            backgroundColor: colors.background.navActive,
          },
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: '40px',
        color: 'inherit',
      },
    },
  },
});

// Button variant factory
const createButtonVariants = (colors) => [
  {
    props: { variant: 'closeDialog' },
    style: {
      color: colors.primary,
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        color: colors.secondary,
      },
    },
  },
  {
    props: { variant: 'deleteDialog' },
    style: {
      color: EVORA_COLORS.error.main,
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'save' },
    style: {
      background: EVORA_COLORS.evoraPurple,
      margin: '3px 0 2px 0',
      padding: '6px 16px',
      marginLeft: 8,
      marginRight: 8,
      color: '#FFF',
      '&:hover': {
        backgroundColor: EVORA_COLORS.darkBlue,
      },
    },
  },
  {
    props: { variant: 'delete' },
    style: {
      background: EVORA_COLORS.error.main,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'print' },
    style: {
      background: EVORA_COLORS.stormBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'release' },
    style: {
      background: EVORA_COLORS.darkOlive,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: EVORA_COLORS.stormBlue,
      },
    },
  },
  {
    props: { variant: 'combine' },
    style: {
      background: EVORA_COLORS.darkBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'copy' },
    style: {
      background: EVORA_COLORS.stormBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: EVORA_COLORS.darkBlue,
      },
    },
  },
  {
    props: { variant: 'move' },
    style: {
      background: EVORA_COLORS.darkBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'split' },
    style: {
      background: EVORA_COLORS.stormBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'unlock' },
    style: {
      background: EVORA_COLORS.warning.main,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'undo' },
    style: {
      background: EVORA_COLORS.stormBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'generatePDF' },
    style: {
      background: EVORA_COLORS.stormBlue,
      color: '#FFF',
      marginLeft: 8,
      marginRight: 8,
      '&:hover': {
        backgroundColor: colors.primary,
      },
    },
  },
  {
    props: { variant: 'signIn' },
    style: {
      background: `linear-gradient(45deg, ${EVORA_COLORS.darkOlive} 30%, ${EVORA_COLORS.stormBlue} 90%)`,
      margin: '3px 0 2px 0',
      width: '55%',
      padding: '6px 16px',
      color: '#FFF',
    },
  },
];

// AppBar variant factory
const createAppBarVariants = (colors, isDark) => [
  {
    props: { variant: 'topAppBar' },
    style: {
      backgroundColor: colors.appBar.background,
      color: colors.appBar.text,
      boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
    },
  },
  {
    props: { variant: 'mobileAppBar' },
    style: {
      backgroundColor: colors.primary,
      position: 'fixed',
      top: 'auto',
      bottom: 0,
    },
  },
  {
    props: { variant: 'breadcrumbs' },
    style: {
      backgroundColor: isDark ? colors.background.paper : EVORA_COLORS.skyBlue,
      position: 'fixed',
      marginTop: '56px',
      '@media (min-width:600px)': {
        marginTop: '64px',
      },
      boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
    },
  },
];

// Link styling factory
const createLinkStyles = (colors) => ({
  defaultProps: {
    underline: 'hover',
    fontSize: '0.8rem',
  },
  styleOverrides: {
    root: {
      '&.Mui-disabled': {
        color: colors.primary,
      },
      '&:hover': {
        color: EVORA_COLORS.stormBlue,
      },
      '&.active': {
        color: colors.secondary,
      },
      textDecoration: 'none',
    },
  },
});

// IconButton variants factory
const createIconButtonVariants = (colors) => [
  {
    props: { variant: 'actionBarActive' },
    style: {
      margin: 0,
      minWidth: 0,
      color: colors.secondary,
    },
  },
  {
    props: { variant: 'actionBarInactive' },
    style: {
      margin: 0,
      minWidth: 0,
      color: '#e8e8e8',
    },
  },
];

// ==========================================
// TENANT LIGHT THEME (Default)
// ==========================================
export const TenantLightTheme = createTheme({
  content: {
    flexGrow: 1,
    padding: '0 25px',
  },
  palette: {
    mode: 'light',
    // Brand purple regardless of what primary is in this palette (white in tenant dark)
    evoraPurple: {
      main: EVORA_COLORS.evoraPurple,
    },
    water: {
      main: EVORA_COLORS.waterBlue,
    },
    background: {
      default: TENANT_LIGHT_COLORS.background.default,
      paper: TENANT_LIGHT_COLORS.background.paper,
      table: TENANT_LIGHT_COLORS.background.paper,
      tableHeader: EVORA_COLORS.skyBlue,
      tableAltRow: 'rgba(107, 114, 174, 0.04)',
      nav: TENANT_LIGHT_COLORS.background.nav,
      navHover: TENANT_LIGHT_COLORS.background.navHover,
      navActive: TENANT_LIGHT_COLORS.background.navActive,
    },
    info: {
      main: EVORA_COLORS.stormBlue,
    },
    success: {
      main: EVORA_COLORS.darkOlive,
    },
    primary: {
      main: TENANT_LIGHT_COLORS.primary,
    },
    secondary: {
      main: TENANT_LIGHT_COLORS.secondary,
    },
    error: {
      main: EVORA_COLORS.error.main,
      dark: EVORA_COLORS.error.dark,
    },
    warning: {
      light: EVORA_COLORS.warning.light,
      main: EVORA_COLORS.warning.main,
      dark: EVORA_COLORS.warning.dark,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    ...sharedTypography,
    h1: {
      color: EVORA_COLORS.stormBlue,
      fontFamily: 'Futura PT',
      fontSize: '2.8rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h2: {
      color: EVORA_COLORS.darkOlive,
      fontFamily: 'Futura PT',
      fontSize: '2.5rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h3: {
      color: EVORA_COLORS.darkOlive,
      fontFamily: 'Futura PT',
      fontSize: '2rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h4: {
      color: EVORA_COLORS.darkOlive,
      fontFamily: 'Futura PT',
      overflow: 'auto',
      marginTop: '5px',
      marginLeft: '7px',
      display: 'inline',
    },
    h5: {
      color: EVORA_COLORS.darkOlive,
      fontFamily: 'Futura PT',
      display: 'inline',
    },
    h6: {
      color: EVORA_COLORS.darkOlive,
      fontFamily: 'Futura PT',
      display: 'inline',
    },
  },
  components: {
    ...sharedComponents,
    ...createNavComponents(TENANT_LIGHT_COLORS),
    MuiAppBar: {
      variants: createAppBarVariants(TENANT_LIGHT_COLORS, false),
    },
    MuiButton: {
      variants: createButtonVariants(TENANT_LIGHT_COLORS),
    },
    MuiIconButton: {
      variants: createIconButtonVariants(TENANT_LIGHT_COLORS),
    },
    MuiLink: createLinkStyles(TENANT_LIGHT_COLORS),
    MuiPaper: {
      variants: [
        {
          props: { variant: 'panel' },
          style: {
            backgroundColor: TENANT_LIGHT_COLORS.background.paper,
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
          },
        },
      ],
    },
  },
});

// ==========================================
// TENANT DARK THEME
// ==========================================
export const TenantDarkTheme = createTheme({
  content: {
    flexGrow: 1,
    padding: '0 25px',
  },
  palette: {
    mode: 'dark',
    // Brand purple regardless of what primary is in this palette (white in tenant dark)
    evoraPurple: {
      main: EVORA_COLORS.evoraPurple,
    },
    water: {
      main: EVORA_COLORS.waterBlueDark,
    },
    text: {
      primary: TENANT_DARK_COLORS.text.primary,
      secondary: TENANT_DARK_COLORS.text.secondary,
    },
    background: {
      default: TENANT_DARK_COLORS.background.default,
      paper: TENANT_DARK_COLORS.background.paper,
      table: '#2A3555',
      tableHeader: EVORA_COLORS.stormBlue,
      tableAltRow: 'rgba(255, 255, 255, 0.04)',
      nav: TENANT_DARK_COLORS.background.nav,
      navHover: TENANT_DARK_COLORS.background.navHover,
      navActive: TENANT_DARK_COLORS.background.navActive,
    },
    primary: {
      main: TENANT_DARK_COLORS.primary,
      contrastText: '#1B223A',
    },
    secondary: {
      main: TENANT_DARK_COLORS.secondary,
      contrastText: '#1B223A',
    },
    error: {
      main: EVORA_COLORS.error.main,
      dark: EVORA_COLORS.error.dark,
    },
    warning: {
      light: EVORA_COLORS.warning.light,
      main: EVORA_COLORS.warning.main,
      dark: EVORA_COLORS.warning.dark,
    },
    contrastThreshold: 4.5,
    tonalOffset: 0.2,
  },
  typography: {
    ...sharedTypography,
    h3: {
      color: EVORA_COLORS.evoraPurple,
    },
    h4: {
      color: EVORA_COLORS.evoraPurple,
    },
    h5: {
      color: EVORA_COLORS.evoraPurple,
    },
    h6: {
      color: EVORA_COLORS.evoraPurple,
    },
  },
  components: {
    ...sharedComponents,
    ...createNavComponents(TENANT_DARK_COLORS),
    MuiAppBar: {
      variants: createAppBarVariants(TENANT_DARK_COLORS, true),
    },
    MuiButton: {
      variants: createButtonVariants(TENANT_DARK_COLORS),
    },
    MuiIconButton: {
      variants: createIconButtonVariants(TENANT_DARK_COLORS),
    },
    MuiLink: createLinkStyles(TENANT_DARK_COLORS),
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      variants: [
        {
          props: { variant: 'panel' },
          style: {
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
          },
        },
      ],
    },
  },
});

// ==========================================
// ADMIN LIGHT THEME
// ==========================================
export const AdminLightTheme = createTheme({
  content: {
    flexGrow: 1,
    padding: '0 25px',
  },
  palette: {
    mode: 'light',
    // Brand purple regardless of what primary is in this palette (white in tenant dark)
    evoraPurple: {
      main: EVORA_COLORS.evoraPurple,
    },
    water: {
      main: EVORA_COLORS.waterBlue,
    },
    background: {
      default: ADMIN_LIGHT_COLORS.background.default,
      paper: ADMIN_LIGHT_COLORS.background.paper,
      table: ADMIN_LIGHT_COLORS.background.paper,
      tableHeader: EVORA_COLORS.skyBlue,
      tableAltRow: 'rgba(56, 84, 109, 0.04)',
      nav: ADMIN_LIGHT_COLORS.background.nav,
      navHover: ADMIN_LIGHT_COLORS.background.navHover,
      navActive: ADMIN_LIGHT_COLORS.background.navActive,
    },
    info: {
      main: EVORA_COLORS.evoraPurple,
    },
    success: {
      main: EVORA_COLORS.darkOlive,
    },
    primary: {
      main: ADMIN_LIGHT_COLORS.primary,
    },
    secondary: {
      main: ADMIN_LIGHT_COLORS.secondary,
    },
    error: {
      main: EVORA_COLORS.error.main,
      dark: EVORA_COLORS.error.dark,
    },
    warning: {
      light: EVORA_COLORS.warning.light,
      main: EVORA_COLORS.warning.main,
      dark: EVORA_COLORS.warning.dark,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    ...sharedTypography,
    h1: {
      color: EVORA_COLORS.stormBlue,
      fontFamily: 'Futura PT',
      fontSize: '2.8rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h2: {
      color: EVORA_COLORS.evoraPurple,
      fontFamily: 'Futura PT',
      fontSize: '2.5rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h3: {
      color: EVORA_COLORS.evoraPurple,
      fontFamily: 'Futura PT',
      fontSize: '2rem',
      fontWeight: '500',
      marginBottom: 10,
    },
    h4: {
      color: EVORA_COLORS.evoraPurple,
      fontFamily: 'Futura PT',
      overflow: 'auto',
      marginTop: '5px',
      marginLeft: '7px',
      display: 'inline',
    },
    h5: {
      color: EVORA_COLORS.evoraPurple,
      fontFamily: 'Futura PT',
      display: 'inline',
    },
    h6: {
      color: EVORA_COLORS.evoraPurple,
      fontFamily: 'Futura PT',
      display: 'inline',
    },
  },
  components: {
    ...sharedComponents,
    ...createNavComponents(ADMIN_LIGHT_COLORS),
    MuiAppBar: {
      variants: createAppBarVariants(ADMIN_LIGHT_COLORS, false),
    },
    MuiButton: {
      variants: createButtonVariants(ADMIN_LIGHT_COLORS),
    },
    MuiIconButton: {
      variants: createIconButtonVariants(ADMIN_LIGHT_COLORS),
    },
    MuiLink: createLinkStyles(ADMIN_LIGHT_COLORS),
    MuiPaper: {
      variants: [
        {
          props: { variant: 'panel' },
          style: {
            backgroundColor: ADMIN_LIGHT_COLORS.background.paper,
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
          },
        },
      ],
    },
  },
});

// ==========================================
// ADMIN DARK THEME
// ==========================================
export const AdminDarkTheme = createTheme({
  content: {
    flexGrow: 1,
    padding: '0 25px',
  },
  palette: {
    mode: 'dark',
    // Brand purple regardless of what primary is in this palette (white in tenant dark)
    evoraPurple: {
      main: EVORA_COLORS.evoraPurple,
    },
    water: {
      main: EVORA_COLORS.waterBlueDark,
    },
    text: {
      primary: ADMIN_DARK_COLORS.text.primary,
      secondary: ADMIN_DARK_COLORS.text.secondary,
    },
    background: {
      default: ADMIN_DARK_COLORS.background.default,
      paper: ADMIN_DARK_COLORS.background.paper,
      table: '#1E2A3A',
      tableHeader: EVORA_COLORS.stormBlue,
      tableAltRow: 'rgba(255, 255, 255, 0.04)',
      nav: ADMIN_DARK_COLORS.background.nav,
      navHover: ADMIN_DARK_COLORS.background.navHover,
      navActive: ADMIN_DARK_COLORS.background.navActive,
    },
    primary: {
      main: ADMIN_DARK_COLORS.primary,
      contrastText: '#0D1321',
    },
    secondary: {
      main: ADMIN_DARK_COLORS.secondary,
      contrastText: '#0D1321',
    },
    error: {
      main: EVORA_COLORS.error.main,
      dark: EVORA_COLORS.error.dark,
    },
    warning: {
      light: EVORA_COLORS.warning.light,
      main: EVORA_COLORS.warning.main,
      dark: EVORA_COLORS.warning.dark,
    },
    contrastThreshold: 4.5,
    tonalOffset: 0.2,
  },
  typography: {
    ...sharedTypography,
    h3: {
      color: EVORA_COLORS.skyBlue,
    },
    h4: {
      color: EVORA_COLORS.skyBlue,
    },
    h5: {
      color: EVORA_COLORS.skyBlue,
    },
    h6: {
      color: EVORA_COLORS.skyBlue,
    },
  },
  components: {
    ...sharedComponents,
    ...createNavComponents(ADMIN_DARK_COLORS),
    MuiAppBar: {
      variants: createAppBarVariants(ADMIN_DARK_COLORS, true),
    },
    MuiButton: {
      variants: createButtonVariants(ADMIN_DARK_COLORS),
    },
    MuiIconButton: {
      variants: createIconButtonVariants(ADMIN_DARK_COLORS),
    },
    MuiLink: createLinkStyles(ADMIN_DARK_COLORS),
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      variants: [
        {
          props: { variant: 'panel' },
          style: {
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
          },
        },
      ],
    },
  },
});

// Default export for backward compatibility
export default TenantLightTheme;
